import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import './ContactUs.css';

const PremiumContact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        preferredContact: 'email'
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [activeTab, setActiveTab] = useState('form');
    const [activeFaq, setActiveFaq] = useState(null);

    // EmailJS service IDs (from .env file)
    const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage(null); // Clear any previous messages

        // Prepare template parameters as per your email template
        const templateParams = {
            user_name: formData.name,
            user_email: formData.email,
            user_phone: formData.phone || 'Not provided',
            user_company: formData.company || 'Not provided',
            subject: formData.subject,
            message: formData.message,
            date: new Date().toLocaleDateString('en-IN', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            company_name: 'BG RealEstate360'
        };

        // Send email using EmailJS
        emailjs.send(
            EMAILJS_SERVICE_ID,
            EMAILJS_TEMPLATE_ID,
            templateParams,
            EMAILJS_PUBLIC_KEY
        )
        .then((response) => {
            console.log('Email sent successfully!', response.status, response.text);
            
            // Success message
            setSubmitMessage({
                type: 'success',
                text: 'Thank you for your message! We will contact you within 24 hours.'
            });

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                subject: '',
                message: '',
                preferredContact: 'email'
            });

            // Auto hide message after 5 seconds
            setTimeout(() => {
                setSubmitMessage(null);
            }, 5000);
        })
        .catch((error) => {
            console.error('Failed to send email:', error);
            
            // Error message
            setSubmitMessage({
                type: 'error',
                text: 'Sorry, there was an error sending your message. Please try again or contact us directly.'
            });
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    const toggleFaq = (index) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    const contactMethods = [
        {
            icon: 'fas fa-phone-alt',
            title: 'Call Us',
            details: ['+1 (555) 123-4567', '+1 (555) 987-6543'],
            action: 'tel:+15551234567',
            color: '#4F46E5'
        },
        {
            icon: 'fas fa-envelope',
            title: 'Email Us',
            details: ['info@company.com', 'support@company.com'],
            action: 'mailto:info@company.com',
            color: '#10B981'
        },
        {
            icon: 'fas fa-comment-dots',
            title: 'Live Chat',
            details: ['Available 24/7', 'Average response: 2 min'],
            action: '#chat',
            color: '#F59E0B'
        },
        {
            icon: 'fas fa-calendar-alt',
            title: 'Book Meeting',
            details: ['Schedule a call', '30-min consultation'],
            action: '#meeting',
            color: '#8B5CF6'
        }
    ];

    const faqItems = [
        {
            question: 'Do you deal in industrial properties for sale as well as rent?',
            answer: 'Yes, we specialize in industrial plots, factories, and warehouses available for both sale and long-term lease based on your business requirements.'
        },
        {
            question: 'What locations do you cover for industrial properties?',
            answer: 'We deal in prime industrial zones including GIDC areas, industrial estates, logistics hubs, and approved industrial corridors.'
        },
        {
            question: 'Are the industrial plots legally approved?',
            answer: 'Yes, all properties we offer are thoroughly verified with clear titles, zoning approvals, and necessary industrial permissions.'
        },
        {
            question: 'Can you help with lease agreements and documentation?',
            answer: 'Absolutely. We assist with lease agreements, sale deeds, registration, and legal documentation to ensure a smooth and secure transaction.'
        },
        {
            question: 'Do you assist with government approvals and compliance?',
            answer: 'Yes, we guide our clients in obtaining required approvals such as GIDC permissions, NA conversion, and other statutory compliances.'
        },
        {
            question: 'How quickly can I get an industrial property?',
            answer: 'Depending on availability and documentation, we can arrange site visits and close deals within a few days for ready-to-move industrial properties.'
        }
    ];

    const getOfficeStatus = () => {
        const now = new Date();
        const istTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
        );

        const day = istTime.getDay(); // 0 = Sunday
        const hour = istTime.getHours();

        // Sunday always closed
        if (day === 0) {
            return {
                text: "Closed (Sunday)",
                className: "pc-closed"
            };
        }

        // Mon–Sat: 9 AM to 6 PM
        if (hour >= 9 && hour < 18) {
            return {
                text: "Open Now",
                className: "pc-open"
            };
        }

        return {
            text: "Closed Now",
            className: "pc-closed"
        };
    };

    return (
        <div className="pc-container">
            {/* Hero Section */}
            <div className="pc-hero">
                <div className="pc-hero-overlay"></div>
                <div className="pc-hero-content">
                    <h1 className="pc-hero-title">Let's Start a Conversation</h1>
                    <p className="pc-hero-subtitle">
                        We're here to help you succeed. Get in touch with our expert team for personalized solutions.
                    </p>
                    <div className="pc-hero-stats">
                        <div className="pc-stat">
                            <div className="pc-stat-number">24/7</div>
                            <div className="pc-stat-label">Support Available</div>
                        </div>
                        <div className="pc-stat-divider"></div>
                        <div className="pc-stat">
                            <div className="pc-stat-number">2h</div>
                            <div className="pc-stat-label">Avg Response Time</div>
                        </div>
                        <div className="pc-stat-divider"></div>
                        <div className="pc-stat">
                            <div className="pc-stat-number">98%</div>
                            <div className="pc-stat-label">Satisfaction Rate</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="pc-main-container">
                {/* Contact Methods Grid */}
                <div className="pc-methods-grid">
                    {contactMethods.map((method, index) => (
                        <a
                            key={index}
                            href={method.action}
                            className="pc-method-card"
                            style={{ '--pc-card-color': method.color }}
                        >
                            <div className="pc-method-icon" style={{ backgroundColor: `${method.color}15` }}>
                                <i className={method.icon} style={{ color: method.color }}></i>
                            </div>
                            <div className="pc-method-content">
                                <h3>{method.title}</h3>
                                {method.details.map((detail, idx) => (
                                    <p key={idx}>{detail}</p>
                                ))}
                            </div>
                            <div className="pc-method-arrow">
                                <i className="fas fa-arrow-right"></i>
                            </div>
                        </a>
                    ))}
                </div>

                {/* Main Content Area */}
                <div className="pc-content-area">
                    {/* Left Panel - Contact Form */}
                    <div className="pc-left-panel">
                        <div className="pc-panel-header">
                            <div className="pc-tab-buttons">
                                <button
                                    className={`pc-tab-btn ${activeTab === 'form' ? 'pc-active' : ''}`}
                                    onClick={() => setActiveTab('form')}
                                >
                                    <i className="fas fa-paper-plane"></i> Send Message
                                </button>
                                <button
                                    className={`pc-tab-btn ${activeTab === 'faq' ? 'pc-active' : ''}`}
                                    onClick={() => setActiveTab('faq')}
                                >
                                    <i className="fas fa-question-circle"></i> Quick FAQ
                                </button>
                            </div>
                        </div>

                        <div className="pc-panel-content contact-page">
                            {activeTab === 'form' ? (
                                <div className="pc-form-wrapper">
                                    <div className="pc-form-header">
                                        <h2>Send us a Message</h2>
                                        <p>Fill out the form below and we'll get back to you shortly</p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="pc-contact-form">
                                        <div className="pc-form-grid">
                                            {/* Name Field */}
                                            <div className="pc-form-group pc-floating-label">
                                                <input
                                                    type="text"
                                                    id="pc-name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder=" "
                                                    className={formData.name ? 'pc-has-value' : ''}
                                                />
                                                <label htmlFor="pc-name">Full Name *</label>
                                                <div className="pc-form-icon">
                                                    <i className="fas fa-user"></i>
                                                </div>
                                            </div>

                                            {/* Email Field */}
                                            <div className="pc-form-group pc-floating-label">
                                                <input
                                                    type="email"
                                                    id="pc-email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder=" "
                                                    className={formData.email ? 'pc-has-value' : ''}
                                                />
                                                <label htmlFor="pc-email">Email Address *</label>
                                                <div className="pc-form-icon">
                                                    <i className="fas fa-envelope"></i>
                                                </div>
                                            </div>

                                            {/* Phone Field */}
                                            <div className="pc-form-group pc-floating-label">
                                                <input
                                                    type="tel"
                                                    id="pc-phone"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder=" "
                                                    className={formData.phone ? 'pc-has-value' : ''}
                                                />
                                                <label htmlFor="pc-phone">Phone Number</label>
                                                <div className="pc-form-icon">
                                                    <i className="fas fa-phone"></i>
                                                </div>
                                            </div>

                                            {/* Company Field */}
                                            <div className="pc-form-group pc-floating-label">
                                                <input
                                                    type="text"
                                                    id="pc-company"
                                                    name="company"
                                                    value={formData.company}
                                                    onChange={handleChange}
                                                    placeholder=" "
                                                    className={formData.company ? 'pc-has-value' : ''}
                                                />
                                                <label htmlFor="pc-company">Company Name</label>
                                                <div className="pc-form-icon">
                                                    <i className="fas fa-building"></i>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Subject Field */}
                                        <div className="pc-form-group pc-floating-label">
                                            <input
                                                type="text"
                                                id="pc-subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                placeholder=" "
                                                className={formData.subject ? 'pc-has-value' : ''}
                                            />
                                            <label htmlFor="pc-subject">Subject *</label>
                                            <div className="pc-form-icon">
                                                <i className="fas fa-tag"></i>
                                            </div>
                                        </div>

                                        {/* Message Field */}
                                        <div className="pc-form-group pc-floating-label">
                                            <textarea
                                                id="pc-message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                rows="4"
                                                placeholder=" "
                                                className={formData.message ? 'pc-has-value' : ''}
                                            />
                                            <label htmlFor="pc-message">Your Message *</label>
                                        </div>

                                        {/* Form Footer */}
                                        <div className="pc-form-footer">
                                            <button
                                                type="submit"
                                                className="pc-submit-btn"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                        Sending Message...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-paper-plane"></i>
                                                        Send Message
                                                    </>
                                                )}
                                            </button>

                                            <div className="pc-form-note">
                                                <i className="fas fa-shield-alt"></i>
                                                Your information is secure and will never be shared
                                            </div>
                                        </div>

                                        {/* Success/Error Message */}
                                        {submitMessage && (
                                            <div className={`pc-message-alert pc-${submitMessage.type}`}>
                                                <i className={`fas fa-${submitMessage.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                                                {submitMessage.text}
                                            </div>
                                        )}
                                    </form>
                                </div>
                            ) : (
                                /* FAQ Section */
                                <div className="pc-faq-section">
                                    <h3>Frequently Asked Questions</h3>
                                    <div className="pc-faq-list">
                                        {faqItems.map((faq, index) => (
                                            <div
                                                key={index}
                                                className={`pc-faq-item ${activeFaq === index ? 'pc-active' : ''}`}
                                                onClick={() => toggleFaq(index)}
                                            >
                                                <div className="pc-faq-question">
                                                    <span>{faq.question}</span>
                                                    <i className="fas fa-chevron-down"></i>
                                                </div>
                                                <div className="pc-faq-answer">
                                                    {faq.answer}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Map & Social */}
                    <div className="pc-right-panel">
                        {/* Map Section */}
                        <div className="pc-map-section">
                            <div className="pc-map-header">
                                <h3><i className="fas fa-map-marker-alt"></i> Visit Our Headquarters</h3>
                                {(() => {
                                    const status = getOfficeStatus();
                                    return (
                                        <div className="pc-map-status">
                                            <span className={`pc-status-indicator ${status.className}`}></span>
                                            {status.text}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="pc-map-wrapper">
                                <div className="pc-map-placeholder">
                                    <iframe
                                        title="Google Map"
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3709.6664496893877!2d73.0216078!3d21.5989392!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be023b449f223a5%3A0x2b53e6061fcd47cc!2sBABA%20GROUP!5e0!3m2!1sen!2sin!4v1768724348329!5m2!1sen!2sin"
                                        width="100%"
                                        height="300"
                                        style={{ border: 0 }}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    ></iframe>

                                </div>
                                <div className="pc-map-info">
                                    <div className="pc-map-detail">
                                        <i className="fas fa-clock"></i>
                                        <div>
                                            <strong>Business Hours</strong>
                                            <p>Mon-Sat: 9:00 AM - 6:00 PM IST</p>
                                        </div>
                                    </div>
                                    <div className="pc-map-detail">
                                        <i className="fas fa-directions"></i>
                                        <div>
                                            <strong>Parking Available</strong>
                                        </div>
                                    </div>
                                    <a
                                        href="https://www.google.com/maps/dir/?api=1&destination=21.5989392,73.0216078&travelmode=driving"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pc-directions-btn"
                                    >
                                        <i className="fas fa-route"></i>
                                        Get Directions
                                    </a>




                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust Indicators */}
                <div className="pc-trust-section">
                    <h3>Trusted By Industry Leaders</h3>
                    <div className="pc-trust-logos">
                        <div className="pc-trust-logo">
                            <i className="fas fa-shield-alt"></i>
                            <span>Enterprise Security</span>
                        </div>
                        <div className="pc-trust-logo">
                            <i className="fas fa-lock"></i>
                            <span>GDPR Compliant</span>
                        </div>
                        <div className="pc-trust-logo">
                            <i className="fas fa-award"></i>
                            <span>Award Winning</span>
                        </div>
                        <div className="pc-trust-logo">
                            <i className="fas fa-check-circle"></i>
                            <span>99.9% Uptime</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PremiumContact;